import React, { useState, useEffect, useContext } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useDispatch } from 'react-redux';
import { CancelTokenSource } from 'axios';
import { RootState } from 'modules';
import { useRecoilValue } from 'recoil';
import scrollIntoView from 'scroll-into-view';
import apiClient, {KAITUSTimerToken } from 'libs/api/apiClient';
import { usePagination, useDataApi } from 'libs/hooks';
import { resetState } from 'modules/learning/createQuiz';
import { quizListFilterState } from 'modules/learning/recoilState';
import { RouteMatch, TListResponse, TPagination } from 'modules/types';
import { CWindowWidth } from 'constants/base.const';
import { Typography } from 'antd';
import styled from 'styled-components';
import { PrimaryColor, device } from 'GlobalStyles';
import quizListTop from 'assets/images/banners/13.webp';
import { ERoute } from 'enums/route.enum';
import { useRouteMatch } from 'react-router-dom';

const FilterTag: any = {
  fit_interview: 'Fit Interview',
  case_interview: 'Case Interview',
  growth_strategy: 'Growth Strategy',
  turnaround_strategy: 'Turnaround Strategy',
  dd_market_sizing: 'DD & Market Sizing',
  operation_optimization: 'Operation Optimization',
  sales_marketing_strategy: 'Sales & Marketing Strategy',
  clarification: 'Clarification',
  structuring: 'Structuring',
  analysis: 'Analysis',
  solution_wrap_up: 'Solution & Wrap up',
  full_drill: '세트문제',
};

type TQuiz = {
  id: number;
  subject: string;
  quiz_types: string;
  is_completed: boolean;
  quiz_number: number;
  title: string;
  hit_count: number;
  is_new: string;
  correct_rate: number;
  is_user_solved: string;
  is_full_drill: boolean;
};

interface ICardBadge {
  quizId: boolean;
}
interface IScreen {
  width: string;
}


const KaitusAssessment = () => {
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const [isFirstRendering, setIsFirstRendering] = useState(true);
  const [quizListModalData, setQuizListModalData]: any = useState({
    visible: false,
  });
  const [filterTagList, setFilterTagList] = useState<Array<string>>([]);
  const [pagination, setPagination] = usePagination(15);
  const [isSearchInput, setIsSearchInput] = useState(false);
  const quizFilters = useRecoilValue(quizListFilterState);
  const { Title, Text } = Typography;
  const dispatch = useDispatch();

  const baseUrl = ERoute.KaitusAssessment
  const match = useRouteMatch(baseUrl);
  const { tenant_id } = (match?.params as RouteMatch) || {};

  const reloadPage = (page?: Partial<TPagination>) => {
    setPagination({
      ...pagination,
      ...page,
    });
    setIsFirstRendering(false);
  };

  const getQuizList = ({ params }: any, cancel?: CancelTokenSource) => {
    return apiClient.get<TQuiz>(`/drill/quizs`, {
      params: params,
      cancelToken: cancel?.token,
    });
  };

  const getPromise = getQuizList.bind(null, {
    params: {
      current: pagination.current,
      pageSize: pagination.pageSize,
      q: pagination.q,
      ordering: '-kaitus_lg',
      is_full_drill: quizFilters.is_full_drill.join('+'),
      subjects: quizFilters.subject.join('+'),
      quiz_types: quizFilters.quiz_types.join('+'),
    },
  });

  const [{ data, loading }, setCallback] = useDataApi<TListResponse<TQuiz>>(getPromise, {
    contents: [],
    last: false,
  });

  useEffect(() => {
    setCallback(() => getPromise);
  }, [quizFilters, pagination]);

  function upToScroll(loading: boolean, isFirstRendering: boolean) {
    if (!loading && !isFirstRendering) {
      scrollIntoView(document?.getElementById('card-list-header') as HTMLElement, {
        align: {
          top: 0.3,
        },
      });
    }
  }

  useEffect(() => {
    upToScroll(loading, isFirstRendering);
  }, [loading]);

  useEffect(() => {
    if (data.contents.length > 0) {
      console.log(data.contents[0].id);
      //KaitusAssessmentModule.start()
      const t = new Date()
      window.localStorage.setItem(KAITUSTimerToken, t.toString());
      window.location.href = `/kaitus/${tenant_id}/assessment/item?operation=view&id=${data.contents[0].id}`;
    }
  }, [data]);

  //어드민 문제 생성할 때 문제 번호, 문제 제목 초기화시켜주기 위해 필요
  useEffect(() => {
    dispatch(resetState());
  }, []);

  return <></>;
};

export default KaitusAssessment;

const maxWidth = CWindowWidth - 300;
const StyledContainer = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .title_wrapper {
    width: 100%;
    height: 400px;
    padding: 0 10px;
    background-image: url(${quizListTop});
    background-size: cover;

    @media screen and ${device.md} {
      height: 205px;
    }

    .pageTitle {
      margin: 150px auto 0 auto;
      font-size: 56px;
      font-weight: 700;

      @media screen and ${device.md} {
        margin-top: 60px;
        font-size: 30px;
      }
    }

    .pageSubtitle {
      margin: 16px auto 0 auto;
      font-size: 14px;

      @media screen and ${device.md} {
        font-size: 10px;
      }
    }

    .ant-typography {
      display: block;
      max-width: ${maxWidth}px;
      color: #ffffff;
    }
  }
`;

const StyledQuizBox = styled.div`
  max-width: ${maxWidth}px;
  width: 97%;

  .card .ant-avatar {
    object-fit: contain;
    height: 200px;
  }

  .card .ant-card-body {
    min-height: 120px;
    padding: 16px 20px;
  }
`;

const StyledQuizTopBadge = styled.div<ICardBadge>`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 10px;
  margin-bottom: 8px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #ffffff;
  background-color: ${({ quizId }) => (quizId ? '#fbbc05' : '#de4949')};
`;

const StyledQuizComplete = styled.div`
  padding: 2px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #3cac40;
`;

const StyledQuizTitle = styled.div`
  display: -webkit-box;
  font-size: 18px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  word-break: break-word;
`;

const StyledTagBox = styled.div`
  max-width: ${maxWidth}px;
  width: 97%;
  height: 40px;
  margin-top: 10px;

  .filter-tag {
    background: transparent;
    border-radius: 8px;
    border: 1px solid ${PrimaryColor};
    padding: 3px 10px;
    color: ${PrimaryColor};
    font-size: 13px;

    .anticon-close {
      color: ${PrimaryColor};
    }
  }

  @media screen and ${device.md} {
    display: none;
  }
`;

const ListWrapper = styled.div`
  & .ant-btn {
    display: block;
    margin-bottom: 20px;
  }
`;

const StyledQuizListHeader = styled.div`
  display: flex;
  position: relative;
  justify-content: space-between;
  align-items: center;
  max-width: ${maxWidth}px;
  width: 97%;
  margin-top: 36px;
  & .ant-input-affix-wrapper {
    transform: translateX(-25%);
    @media screen and ${device.md} {
      transform: unset;
    }
  }
`;

const TotalQuiz = styled.div<IScreen>`
  display: ${({ width }) => width === 'md' && 'none'};
  width: 33%;

  @media screen and ${device.md} {
    display: ${({ width }) => (width === 'md' ? 'block' : 'none')};
    width: 97%;
    margin-top: 20px;
  }
`;

const StyledFilterBtns = styled.div`
  display: flex;
  justify-content: end;

  /* width: 33%; */
`;

const SearchInputArea = styled.div<IScreen>`
  display: flex;
  display: ${({ width }) => width === 'md' && 'none'};
  justify-content: center;
  align-items: center;
  width: 33%;
  width: ${({ width }) => width === 'md' && '40px'};
  height: ${({ width }) => width === 'md' && '40px'};
  border-radius: ${({ width }) => width === 'md' && '7px'};
  background-color: ${({ width }) => width === 'md' && '#f9f9f9'};

  @media screen and ${device.md} {
    display: ${({ width }) => (width === 'md' ? 'flex' : 'none')};
    cursor: pointer;
  }
`;
